import React, { useEffect, useRef, useState } from 'react';
import s from './Profile.module.css';
import { useSelector } from 'react-redux';
import { selectIsUpdating, selectUser } from '../../../../store/selectors/authSelectors';
import Flag from 'react-world-flags';
import { countries } from './countries';
import { useAppDispatch } from '../../../../store/store';
import { UserAttributes } from '../../../../shared/types/authTypes';
import {
    confirmEmailChange,
    requestEmailChange,
    updateUserBio,
} from '../../../../shared/api/authApi';
import IconLoadingSpinner from '../../../../shared/ui/icons/emodji/IconLoadingSpinner';
import VerifyEmailModal from './VerifyEmailModal';

const PersonalInformationForm = () => {
    const user = useSelector(selectUser);
    const isUpdating = useSelector(selectIsUpdating);
    const dispatch = useAppDispatch();

    const [open, setOpen] = useState(false);
    const [verifyModalOpen, setVerifyModalOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(countries.find((c) => c.code === 'GB'));
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');

    const dropdownRef = useRef<HTMLDivElement>(null);

    const [errors, setErrors] = useState<
        Partial<Record<'first_name' | 'last_name' | 'email' | 'phone', string>>
    >({});
    const validateField = (field: string, value: string) => {
        switch (field) {
            case 'first_name':
            case 'last_name':
                if (!/^[\p{L}\p{M}\s'\-\.]+$/u.test(value)) {
                    return 'Only letters, spaces, hyphens, and dots allowed.';
                }
                break;
            case 'email':
                if (!/\S+@\S+\.\S+/.test(value)) {
                    return 'Invalid email format.';
                }
                break;
            case 'phone':
                if (!/^\d{6,15}$/.test(value)) {
                    return 'Phone must be 6–15 digits.';
                }
                break;
            default:
                return '';
        }
        return '';
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (user) {
            setFirstName(user.first_name ?? '');
            setLastName(user.last_name ?? '');
            setEmail(user.email ?? '');
            setPhone(user.phone_number ?? '');
            if (user?.country_code) {
                const found = countries.find((c) => c.dial_code === user.country_code);
                if (found) {
                    setSelectedCountry(found);
                }
            }
        }
    }, [user]);

    const handleSave = async (field: string) => {
        if (!user) return;

        let value = '';
        if (field === 'first_name') value = firstName;
        if (field === 'last_name') value = lastName;
        if (field === 'email') value = email;
        if (field === 'phone') value = phone;

        const error = validateField(field, value);
        if (error) {
            setErrors((prev) => ({ ...prev, [field]: error }));
            return;
        } else {
            setErrors((prev) => ({ ...prev, [field]: undefined }));
        }

        if (field === 'email' && email !== (user.email ?? '')) {
            try {
                await dispatch(requestEmailChange({ id: user.id, email })).unwrap();
                setVerifyModalOpen(true);
            } catch (err: any) {
                if (err && typeof err === 'object') {
                    setErrors((prev) => ({
                        ...prev,
                        email: err.email || 'Failed to request email change',
                    }));
                } else {
                    setErrors((prev) => ({
                        ...prev,
                        email: 'Something went wrong. Please try again.',
                    }));
                }
            }
            return;
        }

        const updates: Partial<UserAttributes> = {};
        if (field === 'first_name' && firstName !== (user.first_name ?? '')) {
            updates.first_name = firstName;
        }
        if (field === 'last_name' && lastName !== (user.last_name ?? '')) {
            updates.last_name = lastName;
        }
        if (
            field === 'phone' &&
            (phone !== (user.phone_number ?? '') ||
                selectedCountry?.dial_code !== user.country_code)
        ) {
            updates.phone_number = phone;
            updates.country_code = selectedCountry?.dial_code ?? null;
        }

        if (Object.keys(updates).length > 0) {
            dispatch(updateUserBio({ id: user.id, attributes: updates }));
        }
    };

    const handleVerifyAccountConfirm = async (code: string) => {
        if (!user) return;
        try {
            await dispatch(confirmEmailChange({ id: user.id, email, code })).unwrap();
            setVerifyModalOpen(false);
        } catch (err: any) {
            if (err && typeof err === 'object') {
                const firstError = Object.values(err)[0] as string;
                throw new Error(firstError || 'Verification failed. Please try again.');
            }
            throw err;
        }
    };

    return (
        <>
            <div className={s.formRow}>
                <div className={s.formGroup}>
                    <label>First name</label>
                    <div className={s.inputWithButton}>
                        <input
                            disabled={isUpdating}
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                        />
                        {firstName !== (user?.first_name ?? '') && (
                            <button
                                disabled={isUpdating}
                                className={s.saveBtn}
                                onClick={() => handleSave('first_name')}
                            >
                                {isUpdating ? (
                                    <span className={s.saveSpinner}>
                                        <IconLoadingSpinner width={16} height={16} />
                                    </span>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        )}
                    </div>
                    {errors.first_name && <span className={s.error}>{errors.first_name}</span>}
                </div>
                <div className={s.formGroup}>
                    <label>Last name</label>
                    <div className={s.inputWithButton}>
                        <input
                            disabled={isUpdating}
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                        />
                        {lastName !== (user?.last_name ?? '') && (
                            <button
                                disabled={isUpdating}
                                className={s.saveBtn}
                                onClick={() => handleSave('last_name')}
                            >
                                {isUpdating ? (
                                    <span className={s.saveSpinner}>
                                        <IconLoadingSpinner width={16} height={16} />
                                    </span>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        )}
                    </div>
                    {errors.last_name && <span className={s.error}>{errors.last_name}</span>}
                </div>
            </div>

            <div className={s.formRow}>
                <div className={s.formGroup}>
                    <label>Email</label>
                    <div className={s.inputWithButton}>
                        <input
                            disabled={isUpdating}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {email !== (user?.email ?? '') && (
                            <button
                                disabled={isUpdating}
                                className={s.saveBtn}
                                onClick={() => handleSave('email')}
                            >
                                {isUpdating ? (
                                    <span className={s.saveSpinner}>
                                        <IconLoadingSpinner width={16} height={16} />
                                    </span>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        )}
                    </div>
                    {errors.email && <span className={s.error}>{errors.email}</span>}
                </div>
                <div className={s.formGroup}>
                    <label>Phone</label>
                    <div className={s.inputWithButton}>
                        <div className={s.phoneInputWrapper} ref={dropdownRef}>
                            <div className={s.phoneInput}>
                                <div className={s.dropdownTrigger} onClick={() => setOpen(!open)}>
                                    <Flag
                                        code={selectedCountry?.code || 'GB'}
                                        style={{ width: 24, height: 16 }}
                                    />
                                    <span className={s.dialCode}>{selectedCountry?.dial_code}</span>
                                    <span className={s.arrow}>▾</span>
                                </div>

                                <input
                                    disabled={isUpdating}
                                    type="tel"
                                    className={s.phoneNumber}
                                    placeholder="123 456 789"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                />
                            </div>

                            {open && (
                                <div className={s.dropdownMenu}>
                                    {countries.map((c) => (
                                        <div
                                            key={c.code}
                                            className={s.dropdownItem}
                                            onClick={() => {
                                                setSelectedCountry(c);
                                                setOpen(false);
                                            }}
                                        >
                                            <Flag code={c.code} style={{ width: 20, height: 14 }} />
                                            <span className={s.countryName}>{c.name}</span>
                                            <span className={s.dialCode}>{c.dial_code}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {phone !== (user?.phone_number ?? '') && (
                            <button
                                disabled={isUpdating}
                                className={s.saveBtn}
                                onClick={() => handleSave('phone')}
                            >
                                Save
                            </button>
                        )}
                    </div>
                    {errors.phone && <span className={s.error}>{errors.phone}</span>}
                </div>
            </div>

            <VerifyEmailModal
                isOpen={verifyModalOpen}
                onClose={() => setVerifyModalOpen(false)}
                onConfirm={handleVerifyAccountConfirm}
                onResendCode={async () => {
                    if (!user) return;
                    await dispatch(requestEmailChange({ id: user.id, email })).unwrap();
                }}
                isLoading={isUpdating}
            />
        </>
    );
};

export default PersonalInformationForm;
